import {
  AppBar,
  Fab,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  AccountCircle,
  ExpandMore,
  ExitToApp,
  Menu as MenuIcon,
} from '@material-ui/icons/';
import clsx from 'clsx';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState, logout } from '../../state/auth';
import { AppState } from '../../state/configureStore';

const useStyles = makeStyles((theme) => ({
  accountMenu: {
    position: 'relative',
    overflow: 'hidden',
    margin: theme.spacing(1),
    padding: theme.spacing(0, 1.25),
    minWidth: 0,
    height: 44,
    borderRadius: 999,
    color: '#fff',
    background:
      'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 100%)',
    border: '1px solid rgba(255,255,255,0.14)',
    boxShadow: '0 10px 28px rgba(7,10,30,0.22)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    textTransform: 'none',
    '&:hover': {
      background:
        'linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(200,235,255,0.12) 60%, rgba(160,220,255,0.06) 100%)',
      boxShadow: '0 16px 38px rgba(7,10,30,0.26)',
    },
  },
  appBar: {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: ({ drawerWidth }: HeaderProps) => ({
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  content: {
    flexGrow: 1,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    marginBottom: theme.spacing(4),
  },
  contentShift: ({ drawerWidth }: HeaderProps) => ({
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: drawerWidth,
  }),
  extendedIcon: {
    marginRight: theme.spacing(1),
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  profileContent: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    zIndex: 1,
  },
  profileAvatar: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    // background: 'linear-gradient(180deg, rgba(255,255,255,0.22), rgba(255,255,255,0.06))',
    // boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.24)',
  },
  profileText: {
    maxWidth: 140,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: 'linear-gradient(90deg,#7ef3a8,#16a34a)',
    boxShadow: '0 0 0 3px rgba(255,255,255,0.08)',
  },
  dropdownIcon: {
    fontSize: 18,
    color: 'rgba(255,255,255,0.85)',
  },
  menuPaper: {
    minWidth: 160,
    background: '#ffffff',
    color: '#1f2937',
    borderRadius: 10,
    boxShadow: '0 10px 30px rgba(2,6,23,0.12)',
    padding: '0px 4px',
  },
  menuItem: {
    borderRadius: 8,
    margin: '4px 8px',
    padding: '8px 12px',
    transition: 'background 160ms ease, transform 160ms ease',
    '&:hover': {
      background: 'rgba(31,41,55,0.06)',
      transform: 'translateY(-1px)',
    },
  },
  root: {
    flexGrow: 1,
  },
  title: {
    flexGrow: 1,
  },
}));

interface HeaderProps {
  drawerWidth: number;
  open: boolean;
  handleDrawerOpen: () => void;
}

const Header: React.FC<HeaderProps> = (props) => {
  const { open, handleDrawerOpen } = props;
  const classes = useStyles(props);
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { displayName } = useSelector<AppState, AuthState>((state) => state.auth);

  const handleProfileMenuOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const isMenuOpen = Boolean(anchorEl);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    handleMenuClose();
  };

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
      elevation={8}
      PaperProps={{ className: classes.menuPaper }}
    >
      <MenuItem className={classes.menuItem} onClick={handleLogout}>
        <ExitToApp style={{ marginRight: 10, fontSize: 18 }} />
        Logout
      </MenuItem>
    </Menu>
  );

  return (
    <>
      <AppBar
        position="static"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: open,
        })}
      >
        <Toolbar>
          <IconButton
            edge="start"
            className={classes.menuButton}
            color="inherit"
            aria-label="menu"
            onClick={handleDrawerOpen}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" className={classes.title}>
            Sales Management
          </Typography>
          {displayName && (
            <Fab
              variant="extended"
              size="small"
              className={classes.accountMenu}
              onClick={handleProfileMenuOpen}
            >
              <span className={classes.profileContent}>
                <span className={classes.profileAvatar}>
                  <AccountCircle className={classes.extendedIcon} />
                </span>
                <span className={classes.profileText}>{displayName}</span>
                <span className={classes.statusDot} />
                <ExpandMore className={classes.dropdownIcon} />
              </span>
            </Fab>
          )}
        </Toolbar>
      </AppBar>
      {renderMenu}
    </>
  );
};

export default Header;
